import { ChevronDown, ChevronUp } from 'lucide-react';
import { FC, ReactNode, createContext, useCallback, useContext, useRef, useState } from 'react';
import { postToast } from 'features/common/utils/postFlash';
import styles from './PunchupAccordion.module.css';

// ---------------------------------------------------------------------------
// Flash types
// ---------------------------------------------------------------------------

/** Closed set of flash/toast outcomes threaded through the whole flash pipeline. */
export type FlashVariant = 'success' | 'error';

export interface FlashOptions {
	title?: string;
	description?: string;
	onComplete?: () => void;
}

const normalizeFlashOptions = (arg?: (() => void) | FlashOptions): FlashOptions =>
	typeof arg === 'function' ? { onComplete: arg } : arg ?? {};

const defaultFlashTitle = (state: FlashVariant) =>
	state === 'success' ? 'Changes saved' : 'Oops, something went wrong';

// ---------------------------------------------------------------------------
// AccordionContext — shared between PunchupAccordionGroup and PunchupAccordionSection
// ---------------------------------------------------------------------------

interface AccordionContextValue {
	openSections: string[];
	toggleSection: (id: string) => void;
	setDirtySection: (id: string, isDirty: boolean) => void;
}

const AccordionContext = createContext<AccordionContextValue>({
	openSections: [],
	toggleSection: () => {},
	setDirtySection: () => {},
});

// ---------------------------------------------------------------------------
// SectionContext — exposes section state and actions to child form components
// ---------------------------------------------------------------------------

export interface SectionContextValue {
	/** Close this accordion section. Safe to call from save/cancel handlers. */
	close: () => void;
	/** Whether this section is currently expanded. */
	isOpen: boolean;
	/**
	 * Fire a toast notification in the parent downstroke frame.
	 * 'success' → green toast; 'error' → destructive toast (also used for deletes).
	 * onComplete fires synchronously after the postMessage is sent.
	 * Accepts either a bare callback (backward-compatible) or a FlashOptions object.
	 */
	flash: (state: FlashVariant, onCompleteOrOptions?: (() => void) | FlashOptions) => void;
	/**
	 * Register/unregister this section's dirty state.
	 * Call setDirty(true) when form has unsaved changes; setDirty(false) on save/cancel/unmount.
	 * When dirty, the user's attempt to close the accordion triggers a shake instead.
	 */
	setDirty: (isDirty: boolean) => void;
	/**
	 * Scroll this accordion item into view.
	 * 'start' (default) = accordion header visible — use after save/cancel.
	 * 'end' = accordion bottom visible — used internally when blocking a dirty close.
	 */
	scrollToSection: (edge?: ScrollLogicalPosition) => void;
	/** True while a form mutation is in-flight. Read by PunchupInlineEditor to show a save overlay. */
	isSaving: boolean;
	/** Written by useAccordionForm to sync isSubmitting → isSaving. Not for direct use in forms. */
	setSaving: (saving: boolean) => void;
}

// Exported so PunchupAccordionSubSection can provide an isolated SectionContext to a sub-panel (a single
// section hosting more than one independent Save-button form). Application code uses the hooks below.
export const SectionContext = createContext<SectionContextValue | null>(null);

/** Used by child form components that are always inside a PunchupAccordionSection. Throws if not. */
export const useAccordionSection = (): SectionContextValue => {
	const ctx = useContext(SectionContext);
	if (!ctx) throw new Error('useAccordionSection must be used inside a PunchupAccordionSection');
	return ctx;
};

/** Safe version — returns null when used outside a section (e.g. tests, Storybook). */
export const useOptionalAccordionSection = () => useContext(SectionContext);

// ---------------------------------------------------------------------------
// PunchupAccordionGroup — controlled, no built-in controls row
// ---------------------------------------------------------------------------

export interface PunchupAccordionGroupProps {
	/** Currently open section IDs (controlled — manage with usePunchupAccordion). */
	openSections: string[];
	/** Called when the user toggles a section trigger. */
	onToggle: (id: string) => void;
	/**
	 * Called when a section's dirty state changes.
	 * Provided by usePunchupAccordion — wire it through so the dashboard can
	 * read hasDirtySections for the navigation Prompt.
	 */
	setDirtySection: (id: string, isDirty: boolean) => void;
	children: ReactNode;
}

/**
 * Context provider + layout container for a group of PunchupAccordionSection children.
 * Fully controlled: pass openSections + onToggle + setDirtySection from usePunchupAccordion.
 * Expand / Collapse all controls are rendered externally via PunchupAccordionControls.
 */
export const PunchupAccordionGroup: FC<PunchupAccordionGroupProps> = ({
	openSections,
	onToggle,
	setDirtySection,
	children,
}) => (
	<AccordionContext.Provider value={{ openSections, toggleSection: onToggle, setDirtySection }}>
		<div className={styles.accordionGroupContainer}>{children}</div>
	</AccordionContext.Provider>
);

// ---------------------------------------------------------------------------
// PunchupAccordionControls — standalone Expand all / Collapse all buttons
// ---------------------------------------------------------------------------

export interface PunchupAccordionControlsProps {
	onExpandAll: () => void;
	onCollapseAll: () => void;
	allExpanded: boolean;
	allCollapsed: boolean;
}

/**
 * The Expand all / Collapse all button pair.
 * Rendered externally (e.g. in a PunchupSplitRow) so callers can compose it
 * alongside other controls without coupling to the accordion's container layout.
 */
export const PunchupAccordionControls: FC<PunchupAccordionControlsProps> = ({
	onExpandAll,
	onCollapseAll,
	allExpanded,
	allCollapsed,
}) => (
	<div className={styles.accordionControlsRow}>
		<button className={styles.controlButton} onClick={onExpandAll} disabled={allExpanded} type="button">
			<ChevronDown size={16} />
			<span className={styles.controlButtonLabel}>Expand all</span>
		</button>
		<button className={styles.controlButton} onClick={onCollapseAll} disabled={allCollapsed} type="button">
			<ChevronUp size={16} />
			<span className={styles.controlButtonLabel}>Collapse all</span>
		</button>
	</div>
);

/* Keep in sync with .accordionItemShaking animation duration (0.4s) in PunchupAccordion.module.css. */
const SHAKE_ANIMATION_MS = 400;

// ---------------------------------------------------------------------------
// PunchupAccordionSection
// ---------------------------------------------------------------------------

export interface PunchupAccordionSectionProps {
	/** Unique identifier — must match an entry in the parent group's allSectionIds. */
	id: string;
	/** Section heading shown in the trigger row. */
	title: string;
	/** Optional sub-heading shown below the title inside the trigger. */
	description?: string;
	/** Optional count badge shown in the trigger row (e.g. number of items). */
	count?: number;
	children: ReactNode;
}

/**
 * Individual collapsible section. Must be rendered inside a PunchupAccordionGroup.
 * Mirrors the CollapsibleSettingsSection component in PunchupLive downstroke.
 *
 * Provides SectionContext to children so forms can:
 * - close() the section on save/cancel
 * - flash('success' | 'error') to give visual feedback
 * - setDirty(bool) to register unsaved changes (blocks accidental close)
 * - scrollToSection() to ensure the section header is in view after an action
 */
export const PunchupAccordionSection: FC<PunchupAccordionSectionProps> = ({
	id,
	title,
	description,
	count,
	children,
}) => {
	const { openSections, toggleSection, setDirtySection } = useContext(AccordionContext);
	const isOpen = openSections.includes(id);

	const itemRef = useRef<HTMLDivElement>(null);
	const [isDirty, setIsDirtyLocal] = useState(false);
	// useState setter is referentially stable, so this doubles as the SectionContext setSaving.
	const [isSaving, setSaving] = useState(false);
	const [isShaking, setIsShaking] = useState(false);

	const flash = useCallback((state: FlashVariant, arg?: (() => void) | FlashOptions) => {
		const { title, description, onComplete } = normalizeFlashOptions(arg);
		postToast(state, title ?? defaultFlashTitle(state), description);
		onComplete?.();
	}, []);

	// Register dirty state. Propagated up to usePunchupAccordion via AccordionContext.
	// Stabilised with useCallback so useAccordionForm's effect can depend on it without re-running.
	const setDirty = useCallback(
		(dirty: boolean) => {
			setIsDirtyLocal(dirty);
			setDirtySection(id, dirty);
		},
		[id, setDirtySection]
	);

	const scrollToSection = (edge: ScrollLogicalPosition = 'start') => {
		itemRef.current?.scrollIntoView({ behavior: 'smooth', block: edge });
	};

	const close = () => {
		if (isOpen) toggleSection(id);
	};

	// Intercept trigger click. When the section is open and has unsaved changes,
	// block the close: shake the item, scroll save/cancel buttons into view, and
	// fire a warning toast in the parent frame so the user knows why it didn't close.
	const handleTriggerClick = () => {
		if (isOpen && isDirty) {
			setIsShaking(true);
			setTimeout(() => setIsShaking(false), SHAKE_ANIMATION_MS);
			scrollToSection('end');
			flash('error', {
				title: 'You have unsaved changes',
				description: 'Please save or discard your changes before closing.',
			});
			return;
		}
		toggleSection(id);
	};

	const itemClassName = [styles.accordionItem, isShaking ? styles.accordionItemShaking : null]
		.filter(Boolean)
		.join(' ');

	return (
		<SectionContext.Provider value={{ close, isOpen, flash, setDirty, scrollToSection, isSaving, setSaving }}>
			<div ref={itemRef} className={itemClassName}>
				<button className={styles.accordionTrigger} onClick={handleTriggerClick} type="button" aria-expanded={isOpen}>
					<div className={styles.accordionTriggerContent}>
						<div className={styles.accordionTitleArea}>
							<div className={styles.accordionTitleRow}>
								<span className={styles.accordionSectionTitle}>{title}</span>
								{isDirty && <span className={styles.unsavedDot} title="Unsaved changes" aria-label="Unsaved changes" />}
							</div>
							{description && <p className={styles.accordionDescription}>{description}</p>}
						</div>
						{count !== undefined && <div className={styles.accordionCountBadge}>{count}</div>}
					</div>
					<ChevronDown
						className={`${styles.accordionChevron}${isOpen ? ` ${styles.isOpen}` : ''}`}
						aria-hidden="true"
					/>
				</button>

				<div className={`${styles.accordionContent}${isOpen ? ` ${styles.isOpen}` : ''}`}>
					<div className={styles.accordionContentInner}>
						<div className={styles.accordionContentPadded}>{children}</div>
					</div>
				</div>
			</div>
		</SectionContext.Provider>
	);
};
